import { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import { Field } from "formik";
import isEqual from "lodash/isEqual";
import isEmpty from "lodash/isEmpty";
import { Modal, Table } from "../../../molecules/index";
import { redTrashIcon } from "../../../../assets/icons";
import { MODALS, MODAL_SIZE } from "../modals.constants";
import { EMPTY_ARRAY, EMPTY_STRING } from "../../../../constants";
import { actions as modalActions } from "../../Modal/modal.reducers";
import { ManageExpression } from "../../../../pages/DataModels/ServiceFields";
import { selectSelectedNode } from "../../../../pages/DataModels";
import {
  UPDATE_TYPE,
  FIELDS,
  UNSET,
} from "../../../../pages/DataModels/datamodels.constants";

const { SELECT_FIELD_MODAL } = MODALS;
const { MD } = MODAL_SIZE;
const { SELECT_QUERY, OBJECT_ID, UPDATES } = FIELDS;

const { setSelectedModal } = modalActions;
const SET = "set";
const KEY_UPDATES = "keyUpdates";

const SelectFieldModal = ({
  name,
  fieldKey,
  values,
  collections,
  btnText,
  title,
  description,
  modelName,
  nodes,
  edges,
  handleChange,
}) => {
  const dispatch = useDispatch();
  const selectedNode = useSelector(selectSelectedNode);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState(EMPTY_ARRAY);
  const [filteredProperties, setFilteredProperties] = useState(EMPTY_ARRAY);
  const [selectedCollection, setSelectedCollection] = useState();
  const [previousProperties, setPreviousProperties] = useState(EMPTY_ARRAY);

  useEffect(() => {
    collections &&
      setSelectedCollection(
        collections.find((collection) => collection.refName === modelName)
      );
  }, [collections, modelName]);

  useEffect(() => {
    const isModalNameChanged =
      selectedCollection &&
      !isEmpty(values[name]) &&
      !values[name].every(({ fieldPath }) =>
        selectedCollection.schema.properties.find(
          (property) => property.schema.key === fieldPath
        )
      );
    if (isModalNameChanged) handleChange(EMPTY_ARRAY, fieldKey);
  }, [values, name, selectedCollection, handleChange, fieldKey]);

  useEffect(() => {
    if (selectedCollection) {
      const collectionProperties = selectedCollection.schema.properties;
      const properties = collectionProperties.filter(
        (property) => !property.meta.isKey
      );
      setFilteredProperties(properties ?? EMPTY_ARRAY);
    }
  }, [selectedCollection, selectedNode]);

  useEffect(() => {
    const hasValues = !isEmpty(values[name]);
    if (hasValues || shouldShowModal) {
      const preSelectedProperties = values[name].reduce(
        (existingProperties, value) => {
          const properties = filteredProperties.find(
            (property) => property.schema.key === value.fieldPath
          );
          if (properties) return [...existingProperties, properties];
          return existingProperties;
        },
        []
      );
      setPreviousProperties(preSelectedProperties);
      setSelectedProperties(preSelectedProperties);
    }
  }, [values, name, filteredProperties, shouldShowModal]);

  const handleShowModal = useCallback(() => {
    dispatch(setSelectedModal(SELECT_FIELD_MODAL));
    setShouldShowModal(true);
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
    setShouldShowModal(false);
  }, [dispatch]);

  const handleAllPropertySelection = (e, rows) => {
    const selectedRows = e.target.checked ? rows : EMPTY_ARRAY;
    setSelectedProperties(selectedRows);
  };

  const getUpdateFields = useCallback(
    (property) => {
      const updateFields = {
        fieldPath: property.schema.key,
        fieldId: uuid(),
        expression: EMPTY_STRING,
      };
      const fieldKeys = Object.keys(values);
      if (fieldKeys.includes(OBJECT_ID) || fieldKeys.includes(SELECT_QUERY))
        return { ...updateFields, updateType: SET };
      else return updateFields;
    },
    [values]
  );

  const handleSelect = useCallback(() => {
    if (name === UPDATES || name === KEY_UPDATES) {
      const updatePropertyObject = selectedProperties.map((property) => {
        return getUpdateFields(property);
      });

      const existingField = values[name].map((property) => property.fieldPath);

      const newFieldsToUpdate = updatePropertyObject.filter(
        (property) => !existingField.includes(property.fieldPath)
      );
      const hasExistingProperties = !isEmpty(values[name]);
      if (hasExistingProperties) {
        const existingProperties = updatePropertyObject.reduce(
          (existingProperties, each) => {
            const properties = values[name].find(
              (property) => property.fieldPath === each.fieldPath
            );
            if (properties) return [...existingProperties, properties];
            return existingProperties;
          },
          []
        );
        handleChange([...existingProperties, ...newFieldsToUpdate], fieldKey);
      } else handleChange(updatePropertyObject, fieldKey);
    } else {
      const updatedPropertiesArray = selectedProperties.reduce(
        (arr, property) => {
          return [
            ...arr,
            {
              fieldPath: property.schema.key,
              fieldId: uuid(),
              type: property.schema.type,
              subtype: property.schema.type,
              updateType: SET,
            },
          ];
        },
        []
      );
      handleChange(updatedPropertiesArray, fieldKey);
    }
    handleClose();
  }, [
    handleClose,
    selectedProperties,
    name,
    values,
    fieldKey,
    handleChange,
    getUpdateFields,
  ]);

  const handleSinglePropertySelection = (e, selectedProperty) => {
    if (e.target.checked)
      setSelectedProperties([...selectedProperties, selectedProperty]);
    else
      setSelectedProperties(
        selectedProperties.filter(
          (property) => property.name !== selectedProperty.name
        )
      );
  };

  const handlePropertyDelete = (id) => {
    handleChange(
      values[name].filter((value) => value.fieldId !== id),
      fieldKey
    );
    setSelectedProperties(
      selectedProperties.filter(
        (property) =>
          property.schema.key !==
          values[name].find((each) => each.fieldId === id).fieldPath
      )
    );
  };

  const headerFormatter = (rows) => {
    const isCheckedAll =
      filteredProperties.length === selectedProperties.length;

    return (
      <div>
        <input
          type="checkbox"
          value={rows}
          defaultChecked={isCheckedAll}
          className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
          onClick={(e) => handleAllPropertySelection(e, rows)}
        />
      </div>
    );
  };

  const propertyFormatter = (row) => (
    <div>
      <input
        type="checkbox"
        value={row}
        checked={selectedProperties.some((each) => each.name === row.name)}
        className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
        onChange={(e) => {
          handleSinglePropertySelection(e, row);
        }}
      />
    </div>
  );

  const getRowDescription = (row) => row.meta.description;

  const columns = [
    {
      dataField: "name",
      formatter: propertyFormatter,
      headerFormatter: headerFormatter,
      text: "Name",
    },
    {
      text: "Type",
      className: "text-center",
      width: 350,
      formatter: getRowDescription,
    },
  ];

  const isSelectButtonDisabled = useMemo(() => {
    return isEqual(new Set(previousProperties), new Set(selectedProperties));
  }, [previousProperties, selectedProperties]);

  const footer = (
    <div className="p-6 border-t border-gray-700 text-right">
      <button
        className="btn btn-primary-light mr-1"
        type="button"
        onClick={handleClose}
      >
        Cancel
      </button>
      <button
        className="relative btn btn-primary"
        type="submit"
        disabled={isSelectButtonDisabled}
        onClick={handleSelect}
      >
        Select
      </button>
    </div>
  );
  return (
    <div>
      {isEmpty(filteredProperties) ? (
        <div className="text-xs p-2 my-1 rounded-md border-2 border-red-500 text-red-600 text-center">
          "No Properties Found"
        </div>
      ) : (
        <button
          type="button"
          className="rounded items-center p-1 w-full btn btn-primary-light"
          onClick={handleShowModal}
        >
          {btnText}
        </button>
      )}
      {shouldShowModal && (
        <Modal
          title={title}
          handleClose={handleClose}
          size={MD}
          footer={footer}
        >
          <div className="p-6 max-h-[70vh] overflow-auto scrollbar">
            <label className="text-gray-300 mb-2 flex">{description}</label>
            <div>
              <Table columns={columns} data={filteredProperties} />
            </div>
          </div>
        </Modal>
      )}
      {values[name].map(({ fieldId, fieldPath, updateType }, index) => (
        <div key={fieldPath}>
          {name === UPDATES || name === KEY_UPDATES ? (
            <div className="my-2">
              <div className="flex flex-row justify-between p-2 mb-2 bg-gray-700 rounded-md">
                <div className="truncate ...">
                  <label className="text-sm font-medium w-full truncate ...">
                    {fieldPath}
                  </label>
                </div>
                {updateType && (
                  <div>
                    <Field
                      as="select"
                      className="form-control h-2"
                      name={`${name}[${index}].updateType`}
                      value={updateType}
                    >
                      {UPDATE_TYPE.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Field>
                  </div>
                )}
                <div>
                  <button
                    className="hover:opacity-80 w-6"
                    type="button"
                    onClick={() => handlePropertyDelete(fieldId)}
                  >
                    <img src={redTrashIcon} alt="delete" className="mt-2 w-5" />
                  </button>
                </div>
              </div>
              {updateType !== UNSET && (
                <ManageExpression
                  name={fieldPath}
                  values={values}
                  collections={collections}
                  expressionKey={`${name}[${index}].expression`}
                  handleChange={(e) =>
                    handleChange(e, `${name}[${index}].expression`)
                  }
                  nodes={nodes}
                  edges={edges}
                />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between p-1 mt-2 mb-1 w-full border border-gray-100 rounded-md">
              <label className="text-sm font-normal overflow-hidden">
                {fieldPath}
              </label>
              <button
                className="w-4 h-4 hover:opacity-80 btn"
                type="button"
                onClick={() => handlePropertyDelete(fieldId)}
              >
                <img src={redTrashIcon} alt="delete" className="w-6" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SelectFieldModal;
